<template>
  <div class="content__area watchlist-page">
    <div class="container-fluid">
      <div class="content__wrapper">
        <div class="content__box">
          <div class="content__box__header">
            <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px;">
              <ul class="top__filter__ul">
                <li class="cursor-default">Watchlist</li>
                <li class="cursor-default opacity--50">Groups and entries</li>
              </ul>
              <div class="d-flex align-items-center flex-wrap" style="gap: 12px;">
                <label class="txt--ss mb-0 d-flex align-items-center watchlist-toggle">
                  <input v-model="includeInactive" type="checkbox" class="mr-2" />
                  <span>Include inactive</span>
                </label>
                <button type="button" class="button button--primary button--sm" @click="reloadAllData" :disabled="loading.groups || loading.entries">
                  Refresh
                </button>
              </div>
            </div>
          </div>

          <div class="content__divider"></div>

          <div class="content__box__body">
            <div v-if="accessDenied" class="watchlist-empty-state">
              <p class="txt--ss mb-2">You do not have access to watchlist management.</p>
              <p class="txt--xs opacity--50 mb-0">Ask an administrator to grant the commercial operator role for this account.</p>
            </div>

            <div v-else class="row watchlist-grid">
              <div class="col-lg-5 mb-4 mb-lg-0">
                <div class="content__box watchlist-panel">
                  <div class="content__box__header pb-2">
                    <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px;">
                      <ul class="top__filter__ul">
                        <li class="cursor-default">Groups</li>
                      </ul>
                      <button type="button" class="button button--primary button--sm" @click="startCreateGroup">
                        New Group
                      </button>
                    </div>
                  </div>
                  <div class="content__divider"></div>
                  <div class="content__box__body watchlist-panel__body">
                    <div class="watchlist-form__section mb-4">
                      <div class="form__group mb-3">
                        <label class="txt--xs txt--off-white mb-2">Group name</label>
                        <input v-model.trim="groupForm.name" type="text" class="textInputField" placeholder="Security Alerts" />
                        <p v-if="groupFieldErrors.name" class="txt--red txt--xs mt-1 mb-0">{{ groupFieldErrors.name }}</p>
                      </div>

                      <div class="row">
                        <div class="col-md-6">
                          <div class="form__group mb-3">
                            <label class="txt--xs txt--off-white mb-2">Priority</label>
                            <select v-model="groupForm.priority" class="textInputField">
                              <option value="high">High</option>
                              <option value="normal">Normal</option>
                              <option value="low">Low</option>
                            </select>
                          </div>
                        </div>
                        <div class="col-md-6 d-flex align-items-end">
                          <label class="txt--xs txt--off-white d-flex align-items-center mb-3 watchlist-toggle">
                            <input v-model="groupForm.is_active" type="checkbox" class="mr-2" />
                            <span>Active</span>
                          </label>
                        </div>
                      </div>

                      <div class="form__group mb-3">
                        <label class="txt--xs txt--off-white mb-2">Notification emails</label>
                        <div class="watchlist-chip-input">
                          <input
                            v-model.trim="notificationEmailInput"
                            type="email"
                            class="textInputField"
                            placeholder="security@company.com"
                            @keyup.enter.prevent="addNotificationEmail"
                          />
                          <button type="button" class="button button--outline button--sm" @click="addNotificationEmail">
                            Add
                          </button>
                        </div>
                        <div v-if="groupForm.notification_emails.length > 0" class="watchlist-chip-list mt-2">
                          <span v-for="email in groupForm.notification_emails" :key="email" class="watchlist-chip">
                            {{ email }}
                            <button type="button" class="watchlist-chip__remove" @click="removeNotificationEmail(email)">x</button>
                          </span>
                        </div>
                        <p v-if="groupFieldErrors.notification_emails" class="txt--red txt--xs mt-1 mb-0">{{ groupFieldErrors.notification_emails }}</p>
                      </div>

                      <div class="form__group mb-3">
                        <label class="txt--xs txt--off-white mb-2">Scope</label>
                        <div class="d-flex flex-wrap" style="gap: 12px;">
                          <label class="txt--xs txt--off-white d-flex align-items-center watchlist-toggle mb-0">
                            <input v-model="groupForm.applies_to_all_properties" :value="true" type="radio" class="mr-2" @change="setGroupScope(true)" />
                            <span>All properties</span>
                          </label>
                          <label class="txt--xs txt--off-white d-flex align-items-center watchlist-toggle mb-0">
                            <input v-model="groupForm.applies_to_all_properties" :value="false" type="radio" class="mr-2" @change="setGroupScope(false)" />
                            <span>Selected properties</span>
                          </label>
                        </div>
                      </div>

                      <div v-if="groupForm.applies_to_all_properties === false" class="form__group mb-3">
                        <div class="d-flex align-items-center justify-content-between mb-2" style="gap: 12px;">
                          <label class="txt--xs txt--off-white mb-0">Select properties</label>
                          <input v-model.trim="propertySearch" type="text" class="textInputField watchlist-search" placeholder="Filter properties" />
                        </div>
                        <div class="watchlist-property-list">
                          <label v-for="property in filteredProperties" :key="property.space_id || property.id" class="watchlist-property-item">
                            <input
                              v-model="groupForm.property_ids"
                              :value="getPropertyId(property)"
                              type="checkbox"
                              class="mr-2"
                            />
                            <span>{{ getPropertyLabel(property) }}</span>
                          </label>
                          <p v-if="filteredProperties.length === 0" class="txt--xs opacity--50 mb-0">No properties match the filter.</p>
                        </div>
                        <p v-if="groupFieldErrors.property_ids" class="txt--red txt--xs mt-1 mb-0">{{ groupFieldErrors.property_ids }}</p>
                      </div>

                      <div class="d-flex flex-wrap" style="gap: 12px;">
                        <button type="button" class="button button--primary button--sm" @click="submitGroupForm" :disabled="loading.groupSubmit">
                          {{ loading.groupSubmit ? 'Saving...' : (editingGroupId ? 'Update Group' : 'Create Group') }}
                        </button>
                        <button v-if="editingGroupId || hasGroupFormChanges" type="button" class="button button--outline button--sm" @click="resetGroupForm">
                          Clear
                        </button>
                      </div>
                    </div>

                    <div class="table-responsive">
                      <table class="table table__audit table-borderless">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Priority</th>
                            <th>Recipients</th>
                            <th>Scope</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody v-if="groups.length > 0">
                          <tr
                            v-for="group in groups"
                            :key="group._id"
                            :class="{ selected: group._id === selectedGroupId }"
                            @click="selectGroup(group)"
                          >
                            <td>
                              <div class="d-flex flex-column">
                                <span>{{ group.name }}</span>
                                <span class="txt--xs opacity--50">{{ group._id }}</span>
                              </div>
                            </td>
                            <td>
                              <span :class="priorityClass(group.priority)" class="watchlist-priority-tag">
                                {{ displayPriority(group.priority) }}
                              </span>
                            </td>
                            <td>
                              <span>{{ displayRecipients(group.notification_emails) }}</span>
                            </td>
                            <td>{{ displayGroupScope(group) }}</td>
                            <td :class="group.is_active ? 'txt--green' : 'txt--red'">
                              {{ group.is_active ? 'Active' : 'Inactive' }}
                            </td>
                            <td>
                              <button type="button" class="watchlist-row-action mr-3" @click.stop="startEditGroup(group)">Edit</button>
                              <button type="button" class="watchlist-row-action txt--red" @click.stop="confirmDeleteGroup(group)">Delete</button>
                            </td>
                          </tr>
                        </tbody>
                        <tbody v-else>
                          <tr>
                            <td colspan="6"><p class="text-center mb-0">No watchlist groups found.</p></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-7">
                <div class="content__box watchlist-panel">
                  <div class="content__box__header pb-2">
                    <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 12px;">
                      <ul class="top__filter__ul">
                        <li class="cursor-default">Entries</li>
                      </ul>
                      <p class="txt--ss mb-0 opacity--50" v-if="selectedGroup">
                        {{ selectedGroup.name }}
                      </p>
                    </div>
                  </div>
                  <div class="content__divider"></div>
                  <div class="content__box__body watchlist-panel__body">
                    <div v-if="!selectedGroup" class="watchlist-empty-state">
                      <p class="txt--ss mb-0">Select a watchlist group to manage entries.</p>
                    </div>

                    <template v-else>
                      <div class="watchlist-form__section mb-4">
                        <div class="d-flex align-items-center justify-content-between flex-wrap mb-3" style="gap: 12px;">
                          <div>
                            <p class="txt--ss mb-1">{{ editingEntryId ? 'Edit entry' : 'Add entry' }}</p>
                            <p class="txt--xs opacity--50 mb-0">License plates are normalized before submission, but the backend remains the source of truth.</p>
                          </div>
                          <span :class="priorityClass(selectedGroup.priority)" class="watchlist-priority-tag">
                            {{ displayPriority(selectedGroup.priority) }}
                          </span>
                        </div>

                        <div class="row">
                          <div class="col-md-4">
                            <div class="form__group mb-3">
                              <label class="txt--xs txt--off-white mb-2">License plate</label>
                              <input
                                v-model.trim="entryForm.license_plate"
                                type="text"
                                class="textInputField"
                                placeholder="ABC1234"
                                @input="entryForm.license_plate = normalizePlateInput(entryForm.license_plate)"
                              />
                              <p v-if="entryFieldErrors.license_plate" class="txt--red txt--xs mt-1 mb-0">{{ entryFieldErrors.license_plate }}</p>
                            </div>
                          </div>
                          <div class="col-md-4">
                            <div class="form__group mb-3">
                              <label class="txt--xs txt--off-white mb-2">Label</label>
                              <input v-model.trim="entryForm.label" type="text" class="textInputField" placeholder="Known trespass vehicle" />
                            </div>
                          </div>
                          <div class="col-md-4 d-flex align-items-end">
                            <label class="txt--xs txt--off-white d-flex align-items-center mb-3 watchlist-toggle">
                              <input v-model="entryForm.is_active" type="checkbox" class="mr-2" />
                              <span>Active</span>
                            </label>
                          </div>
                        </div>

                        <div class="form__group mb-3">
                          <label class="txt--xs txt--off-white mb-2">Notes</label>
                          <textarea v-model.trim="entryForm.notes" class="textInputField watchlist-notes" rows="3" placeholder="Call onsite security immediately."></textarea>
                        </div>

                        <div class="d-flex flex-wrap" style="gap: 12px;">
                          <button type="button" class="button button--primary button--sm" @click="submitEntryForm" :disabled="loading.entrySubmit">
                            {{ loading.entrySubmit ? 'Saving...' : (editingEntryId ? 'Update Entry' : 'Create Entry') }}
                          </button>
                          <button v-if="editingEntryId || hasEntryFormChanges" type="button" class="button button--outline button--sm" @click="resetEntryForm">
                            Clear
                          </button>
                        </div>
                      </div>

                      <div class="table-responsive">
                        <table class="table table__audit table-borderless">
                          <thead>
                            <tr>
                              <th>Plate</th>
                              <th>Label</th>
                              <th>Notes</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody v-if="entries.length > 0">
                            <tr v-for="entry in entries" :key="entry._id">
                              <td>{{ entry.license_plate }}</td>
                              <td>{{ entry.label || '-' }}</td>
                              <td class="watchlist-notes-cell">{{ entry.notes || '-' }}</td>
                              <td :class="entry.is_active ? 'txt--green' : 'txt--red'">
                                {{ entry.is_active ? 'Active' : 'Inactive' }}
                              </td>
                              <td>
                                <button type="button" class="watchlist-row-action mr-3" @click.stop="startEditEntry(entry)">Edit</button>
                                <button type="button" class="watchlist-row-action txt--red" @click.stop="confirmDeleteEntry(entry)">Delete</button>
                              </td>
                            </tr>
                          </tbody>
                          <tbody v-else>
                            <tr>
                              <td colspan="5"><p class="text-center mb-0">No entries found for this group.</p></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  createWatchlistEntry,
  createWatchlistGroup,
  deleteWatchlistEntry,
  deleteWatchlistGroup,
  extractApiErrorMessage,
  extractValidationErrors,
  listWatchlistEntries,
  listWatchlistGroups,
  normalizeWatchlistLicensePlate,
  normalizeWatchlistPriority,
  updateWatchlistEntry,
  updateWatchlistGroup,
} from "@/services/watchlist.service";

function buildDefaultGroupForm() {
  return {
    name: "",
    priority: "normal",
    notification_emails: [],
    property_ids: [],
    applies_to_all_properties: true,
    is_active: true,
  };
}

function buildDefaultEntryForm() {
  return {
    license_plate: "",
    label: "",
    notes: "",
    is_active: true,
  };
}

export default {
  name: "Watchlist",
  data() {
    return {
      accessDenied: false,
      includeInactive: false,
      groups: [],
      entries: [],
      properties: [],
      selectedGroupId: "",
      editingGroupId: null,
      editingEntryId: null,
      notificationEmailInput: "",
      propertySearch: "",
      groupForm: buildDefaultGroupForm(),
      entryForm: buildDefaultEntryForm(),
      groupFieldErrors: {},
      entryFieldErrors: {},
      loading: {
        groups: false,
        entries: false,
        groupSubmit: false,
        entrySubmit: false,
      },
    };
  },
  computed: {
    selectedGroup() {
      return this.groups.find((group) => group._id === this.selectedGroupId) || null;
    },
    filteredProperties() {
      const searchTerm = this.propertySearch.trim().toLowerCase();
      if (!searchTerm) {
        return this.properties;
      }

      return this.properties.filter((property) => {
        const label = this.getPropertyLabel(property).toLowerCase();
        return label.includes(searchTerm);
      });
    },
    hasGroupFormChanges() {
      return Boolean(
        this.groupForm.name.trim() ||
        this.groupForm.notification_emails.length > 0 ||
        this.groupForm.property_ids.length > 0 ||
        this.editingGroupId !== null ||
        this.groupForm.priority !== "normal" ||
        this.groupForm.is_active === false ||
        this.groupForm.applies_to_all_properties === false
      );
    },
    hasEntryFormChanges() {
      return Boolean(
        this.entryForm.license_plate.trim() ||
        this.entryForm.label.trim() ||
        this.entryForm.notes.trim() ||
        this.editingEntryId !== null ||
        this.entryForm.is_active === false
      );
    },
  },
  created() {
    this.reloadAllData();
  },
  watch: {
    includeInactive() {
      this.reloadAllData();
    },
  },
  methods: {
    normalizePlateInput(value) {
      return normalizeWatchlistLicensePlate(value);
    },
    priorityClass(priority) {
      const normalizedPriority = normalizeWatchlistPriority(priority);
      return `watchlist-priority-tag--${normalizedPriority}`;
    },
    displayPriority(priority) {
      const normalizedPriority = normalizeWatchlistPriority(priority);
      return normalizedPriority.charAt(0).toUpperCase() + normalizedPriority.slice(1);
    },
    displayRecipients(recipients) {
      return Array.isArray(recipients) && recipients.length > 0 ? recipients.join(", ") : "-";
    },
    displayGroupScope(group) {
      if (group && group.applies_to_all_properties) {
        return "All properties";
      }

      const count = Array.isArray(group && group.property_ids) ? group.property_ids.length : 0;
      return count > 0 ? `${count} selected` : "Selected properties";
    },
    getPropertyId(property) {
      return property.space_id || property.id || property._id || "";
    },
    getPropertyLabel(property) {
      const propertyName = property && property.property_name ? String(property.property_name).trim() : "";
      const address = property && property.address ? String(property.address).trim() : "";

      if (propertyName && address) {
        return `${propertyName} - ${address}`;
      }

      return propertyName || address || this.getPropertyId(property);
    },
    resetGroupErrors() {
      this.groupFieldErrors = {};
    },
    resetEntryErrors() {
      this.entryFieldErrors = {};
    },
    resetGroupForm() {
      this.editingGroupId = null;
      this.groupForm = buildDefaultGroupForm();
      this.notificationEmailInput = "";
      this.resetGroupErrors();
    },
    resetEntryForm() {
      this.editingEntryId = null;
      this.entryForm = buildDefaultEntryForm();
      this.resetEntryErrors();
    },
    setGroupScope(isAllProperties) {
      this.groupForm.applies_to_all_properties = isAllProperties;
      if (isAllProperties) {
        this.groupForm.property_ids = [];
      }
    },
    addNotificationEmail() {
      const email = String(this.notificationEmailInput || "").trim().toLowerCase();
      if (!email) {
        return;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        this.$toasted.show("Please provide a valid email address.", { duration: 3000 });
        return;
      }

      if (!this.groupForm.notification_emails.includes(email)) {
        this.groupForm.notification_emails.push(email);
      }

      this.notificationEmailInput = "";
    },
    removeNotificationEmail(email) {
      this.groupForm.notification_emails = this.groupForm.notification_emails.filter((value) => value !== email);
    },
    buildGroupPayload() {
      return {
        name: this.groupForm.name.trim(),
        priority: normalizeWatchlistPriority(this.groupForm.priority),
        notification_emails: [...new Set(this.groupForm.notification_emails)],
        property_ids: this.groupForm.applies_to_all_properties ? [] : [...new Set(this.groupForm.property_ids)],
        is_active: Boolean(this.groupForm.is_active),
      };
    },
    buildEntryPayload() {
      return {
        license_plate: normalizeWatchlistLicensePlate(this.entryForm.license_plate),
        label: this.entryForm.label.trim(),
        notes: this.entryForm.notes.trim(),
        is_active: Boolean(this.entryForm.is_active),
      };
    },
    async reloadAllData() {
      await this.fetchProperties();
      await this.fetchGroups();
    },
    async fetchProperties() {
      try {
        const response = await this.axios.post(window.hostname + "api/auth/all-space", {
          limit: 500,
          device_type: 1,
          space_type: 1,
          status: 0,
          page: 1,
          minify: 1,
          minify_2: 0,
          time_zone: this.$moment.tz.guess(new Date()),
        });

        this.properties = response.data && response.data.status ? response.data.data.all_space || [] : [];
      } catch (error) {
        this.properties = [];
      }
    },
    async fetchGroups() {
      this.loading.groups = true;
      this.accessDenied = false;

      try {
        this.groups = await listWatchlistGroups(this.includeInactive);
        if (this.groups.length === 0) {
          this.selectedGroupId = "";
          this.entries = [];
          this.resetEntryForm();
          return;
        }

        const selectedGroupStillExists = this.groups.some((group) => group._id === this.selectedGroupId);
        if (!selectedGroupStillExists) {
          this.selectedGroupId = this.groups[0]._id;
        }

        await this.fetchEntries(this.selectedGroupId);
      } catch (error) {
        if (error && error.response && error.response.status === 403) {
          this.accessDenied = true;
          this.groups = [];
          this.entries = [];
          this.selectedGroupId = "";
          return;
        }

        if (error && error.response && error.response.status !== 401) {
          this.$toasted.show(extractApiErrorMessage(error), { duration: 3500 });
        }
      } finally {
        this.loading.groups = false;
      }
    },
    async fetchEntries(groupId) {
      if (!groupId) {
        this.entries = [];
        return;
      }

      this.loading.entries = true;
      try {
        this.entries = await listWatchlistEntries(groupId, this.includeInactive);
      } catch (error) {
        if (error && error.response && error.response.status === 403) {
          this.accessDenied = true;
          this.entries = [];
          return;
        }

        if (error && error.response && error.response.status === 404) {
          this.$toasted.show("Selected watchlist group was not found. Refreshing data.", { duration: 3500 });
          await this.fetchGroups();
          return;
        }

        if (error && error.response && error.response.status !== 401) {
          this.$toasted.show(extractApiErrorMessage(error), { duration: 3500 });
        }
      } finally {
        this.loading.entries = false;
      }
    },
    selectGroup(group) {
      if (!group || group._id === this.selectedGroupId) {
        return;
      }

      this.selectedGroupId = group._id;
      this.resetEntryForm();
      this.fetchEntries(group._id);
    },
    startCreateGroup() {
      this.resetGroupForm();
      this.groupForm.applies_to_all_properties = true;
    },
    startEditGroup(group) {
      this.selectedGroupId = group._id;
      this.editingGroupId = group._id;
      this.groupForm = {
        name: group.name || "",
        priority: normalizeWatchlistPriority(group.priority),
        notification_emails: Array.isArray(group.notification_emails) ? [...group.notification_emails] : [],
        property_ids: Array.isArray(group.property_ids) ? [...group.property_ids] : [],
        applies_to_all_properties: Boolean(group.applies_to_all_properties) || !(Array.isArray(group.property_ids) && group.property_ids.length > 0),
        is_active: group.is_active !== false,
      };
      this.notificationEmailInput = "";
      this.resetGroupErrors();
      this.fetchEntries(group._id);
    },
    async submitGroupForm() {
      if (!this.groupForm.name.trim()) {
        this.groupFieldErrors = { name: "Group name is required." };
        return;
      }

      if (this.groupForm.notification_emails.length === 0) {
        this.groupFieldErrors = { notification_emails: "At least one notification email is required." };
        return;
      }

      this.loading.groupSubmit = true;
      this.resetGroupErrors();

      const payload = this.buildGroupPayload();

      try {
        const response = this.editingGroupId
          ? await updateWatchlistGroup(this.editingGroupId, payload)
          : await createWatchlistGroup(payload);

        const savedGroup = response && response.data ? response.data.data : null;
        this.$toasted.show(response.data.message || "Watchlist group saved.", { duration: 3000 });
        this.resetGroupForm();
        await this.fetchGroups();

        if (savedGroup && savedGroup._id) {
          this.selectedGroupId = savedGroup._id;
          await this.fetchEntries(savedGroup._id);
        }
      } catch (error) {
        if (error && error.response && error.response.status === 422) {
          this.groupFieldErrors = extractValidationErrors(error);
          return;
        }

        if (error && error.response && error.response.status === 404) {
          this.$toasted.show("Watchlist group was not found. Refreshing data.", { duration: 3500 });
          await this.fetchGroups();
          return;
        }

        this.$toasted.show(extractApiErrorMessage(error), { duration: 3500 });
      } finally {
        this.loading.groupSubmit = false;
      }
    },
    confirmDeleteGroup(group) {
      this.$swal({
        title: "Delete group?",
        text: `Delete ${group.name} and all of its entries?`,
        showCancelButton: true,
        confirmButtonText: "Delete",
      }).then((result) => {
        if (result.value) {
          this.deleteGroup(group._id);
        }
      });
    },
    async deleteGroup(groupId) {
      try {
        await deleteWatchlistGroup(groupId);
        this.$toasted.show("Watchlist group deleted.", { duration: 3000 });
        if (this.selectedGroupId === groupId) {
          this.selectedGroupId = "";
          this.entries = [];
        }
        await this.fetchGroups();
      } catch (error) {
        if (error && error.response && error.response.status === 404) {
          await this.fetchGroups();
          return;
        }

        this.$toasted.show(extractApiErrorMessage(error), { duration: 3500 });
      }
    },
    startEditEntry(entry) {
      this.editingEntryId = entry._id;
      this.entryForm = {
        license_plate: entry.license_plate || "",
        label: entry.label || "",
        notes: entry.notes || "",
        is_active: entry.is_active !== false,
      };
      this.resetEntryErrors();
    },
    async submitEntryForm() {
      if (!this.selectedGroupId) {
        this.$toasted.show("Please select a watchlist group first.", { duration: 3000 });
        return;
      }

      if (!this.entryForm.license_plate.trim()) {
        this.entryFieldErrors = { license_plate: "License plate is required." };
        return;
      }

      this.loading.entrySubmit = true;
      this.resetEntryErrors();

      const payload = this.buildEntryPayload();

      try {
        const response = this.editingEntryId
          ? await updateWatchlistEntry(this.selectedGroupId, this.editingEntryId, payload)
          : await createWatchlistEntry(this.selectedGroupId, payload);

        this.$toasted.show(response.data.message || "Watchlist entry saved.", { duration: 3000 });
        this.resetEntryForm();
        await this.fetchEntries(this.selectedGroupId);
      } catch (error) {
        if (error && error.response && error.response.status === 422) {
          this.entryFieldErrors = extractValidationErrors(error);
          return;
        }

        if (error && error.response && error.response.status === 404) {
          this.$toasted.show("Watchlist entry was not found. Refreshing data.", { duration: 3500 });
          await this.fetchEntries(this.selectedGroupId);
          await this.fetchGroups();
          return;
        }

        this.$toasted.show(extractApiErrorMessage(error), { duration: 3500 });
      } finally {
        this.loading.entrySubmit = false;
      }
    },
    confirmDeleteEntry(entry) {
      this.$swal({
        title: "Delete entry?",
        text: `Delete ${entry.license_plate} from this group?`,
        showCancelButton: true,
        confirmButtonText: "Delete",
      }).then((result) => {
        if (result.value) {
          this.deleteEntry(entry._id);
        }
      });
    },
    async deleteEntry(entryId) {
      try {
        await deleteWatchlistEntry(this.selectedGroupId, entryId);
        this.$toasted.show("Watchlist entry deleted.", { duration: 3000 });
        await this.fetchEntries(this.selectedGroupId);
      } catch (error) {
        if (error && error.response && error.response.status === 404) {
          await this.fetchEntries(this.selectedGroupId);
          return;
        }

        this.$toasted.show(extractApiErrorMessage(error), { duration: 3500 });
      }
    },
  },
};
</script>

<style scoped>
.watchlist-page {
  padding-bottom: 24px;
}

.watchlist-grid {
  align-items: flex-start;
}

.watchlist-panel {
  min-height: 100%;
}

.watchlist-panel__body {
  max-height: none;
}

.watchlist-empty-state {
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
}

.watchlist-toggle {
  cursor: pointer;
}

.watchlist-chip-input {
  display: flex;
  align-items: center;
  gap: 12px;
}

.watchlist-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.watchlist-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 12px;
}

.watchlist-chip__remove {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.watchlist-property-list {
  max-height: 220px;
  overflow: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 12px;
}

.watchlist-property-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 13px;
  cursor: pointer;
}

.watchlist-property-item:last-child {
  margin-bottom: 0;
}

.watchlist-priority-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.watchlist-priority-tag--high {
  background: rgba(220, 53, 69, 0.16);
  color: #ff808d;
}

.watchlist-priority-tag--normal {
  background: rgba(255, 193, 7, 0.16);
  color: #ffd966;
}

.watchlist-priority-tag--low {
  background: rgba(40, 167, 69, 0.16);
  color: #7cd992;
}

.watchlist-row-action {
  border: 0;
  background: transparent;
  color: #7cd992;
  padding: 0;
  cursor: pointer;
}

.watchlist-search {
  max-width: 220px;
}

.watchlist-notes {
  min-height: 92px;
  resize: vertical;
}

.watchlist-notes-cell {
  max-width: 320px;
  white-space: normal;
}

@media (max-width: 991px) {
  .watchlist-chip-input {
    flex-direction: column;
    align-items: stretch;
  }

  .watchlist-search {
    max-width: 100%;
  }
}
</style>